/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIsOrgMismatch } from 'containers/Organizations/hooks/useIsOrgMismatch';
import useKeyboardEventHandler from 'hooks/useKeyboardEventHandler';

// MUI Components
import AppBar from '@mui/material/AppBar';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import MoreIcon from '@mui/icons-material/MoreVert';
import Toolbar from '@mui/material/Toolbar';
import {
  GridFilterModel,
  GridPaginationModel,
  GridRowId,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid';

// Our Components
import AdvSearchDialog from 'containers/Base/dialogs/AdvSearch';
import ConfirmationDialog from 'components/ConfirmationDialog';
import DataGrid from 'containers/Base/components/DataGrid';
import ImportDialog from 'containers/Base/dialogs/Import';
import MobileMenu from 'containers/Base/components/MobileMenu';
import NewDialog from 'containers/Base/dialogs/New';
import SearchWidget from 'containers/Base/components/SearchWidget';
import StandardMenu from 'containers/Base/components/StandardMenu';
import UpdateDialog from 'containers/Base/dialogs/Update';

// Types
import type { TSchema } from 'types/items';
import type { TOperators, TSearchFilterRow } from 'types/search';
import type { Configuration } from 'containers/Schema/schema.types';
import type { WorkingState } from 'containers/Data/data.types';

import type { DocumentStub } from 'containers/Docs/docs.types';
import { getPluralLabel } from './utils/pluralize';

const emptyGridRowSelectionModel = (): GridRowSelectionModel => ({
  type: 'include',
  ids: new Set<GridRowId>(),
});

const gridRowSelectionInclude = (
  ids: Iterable<GridRowId>
): GridRowSelectionModel => ({
  type: 'include',
  ids: new Set(ids),
});

type BaseDataHandler = (
  configuration: Configuration,
  disablePagination: boolean,
  forceRefresh: boolean,
  filterModel: GridFilterModel | undefined,
  globalSearch: string,
  paginationModel: GridPaginationModel | undefined,
  rows: any[],
  schema: TSchema<any>,
  sortModel: GridSortModel | undefined
) => void;

type Props = {
  authtoken: string;
  configuration: Configuration;
  data: {
    values?: any[];
    rowCount?: number;
    lastUpdated?: Date | null;
  };
  gridHeightCss?: string;
  onDataAdd: BaseDataHandler;
  onDataChange: BaseDataHandler;
  onDataClear: (configuration: Configuration) => void;
  onDataDelete: BaseDataHandler;
  onDataExport: BaseDataHandler;
  onDataRefresh: (
    configuration: Configuration,
    disablePagination: boolean,
    forceRefresh: boolean,
    filterModel: GridFilterModel | undefined,
    globalSearch: string,
    paginationModel: GridPaginationModel | undefined,
    sortModel: GridSortModel | undefined
  ) => void;
  onDocView: (doc: DocumentStub) => void;
  onSchemaRefresh: (configuration: Configuration) => void;
  schema: TSchema<any>;
  workingState: WorkingState;
};

export function Base({
  authtoken,
  configuration,
  data,
  gridHeightCss,
  onDataAdd,
  onDataChange,
  onDataClear,
  onDataDelete,
  onDataExport,
  onDataRefresh,
  onDocView,
  onSchemaRefresh,
  schema,
  workingState,
}: Props) {
  // State
  const [advSearchFilterCount, setAdvSearchFilterCount] = useState(0);
  const [disablePagination, setDisablePagination] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [globalSearch, setGlobalSearch] = useState('');
  const [fields, setFields] = useState<any[]>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>(
    undefined
  );
  const [isOrgMismatch, resolveMismatch] = useIsOrgMismatch();
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    useState<null | HTMLElement>(null);
  const [paginationModel, setPaginationModel] = useState<
    GridPaginationModel | undefined
  >(undefined);
  const [rowsSelected, setRowsSelected] = useState<boolean>(false);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [searchFilters, setSearchFilters] = useState<TSearchFilterRow[]>([]);
  const [showAdvSearchDialog, setShowAdvSearchDialog] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showPayrollExportDialog, setShowPayrollExportDialog] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(
    undefined
  );
  const prevWorkingStateRef = useRef<WorkingState>({
    itemType: '',
    adding: false,
    deleting: false,
    error: false,
    loading: false,
    updating: false,
  });
  const prevDataLastUpdatedRef = useRef<Date | null>(null);

  // Handlers
  const handleAdd = (rows: any) => {
    onDataAdd(
      configuration,
      disablePagination,
      forceRefresh,
      filterModel,
      globalSearch,
      paginationModel,
      rows,
      schema,
      sortModel
    );
  };
  const handleExport = () => {
    onDataExport(
      configuration,
      disablePagination,
      forceRefresh,
      filterModel,
      globalSearch,
      paginationModel,
      selectedRows,
      schema,
      sortModel
    );
  };
  const handleUpdate = (rows: any) => {
    onDataChange(
      configuration,
      disablePagination,
      forceRefresh,
      filterModel,
      globalSearch,
      paginationModel,
      rows,
      schema,
      sortModel
    );
  };
  const handleAdvSearchSave = () => {
    setAdvSearchFilterCount(searchFilters.length);
    // Map the searchFilters state to a GridFilterModel object
    const newFilterModel: GridFilterModel = {
      items: searchFilters.map((filter) => ({
        field: filter.fieldName,
        operator: filter.operator,
        value: filter.value || filter.values.join(','),
      })),
    };
    setFilterModel(newFilterModel);
    setShowAdvSearchDialog(false);
  };
  const handleCancelUpdate = () => {
    setShowUpdateDialog(false);
    setRowsSelected(false);
    setSelectedRows([]);
    setRowSelectionModel(emptyGridRowSelectionModel());
  };
  const handleDeleteRows = (rowsToDelete: any[] = selectedRows) => {
    onDataDelete(
      configuration,
      disablePagination,
      forceRefresh,
      filterModel,
      globalSearch,
      paginationModel,
      rowsToDelete,
      schema,
      sortModel
    );
  };
  const handleConfirmDelete = () => {
    setShowConfirmDelete(false);
    handleDeleteRows();
  };
  const setShowDeleteConfirmation = (show: boolean) => {
    if (!show) {
      setShowConfirmDelete(false);
      setShowDeleteDialog(false);
      return;
    }
    if (configuration.customDialogs?.delete) {
      setShowDeleteDialog(true);
    } else {
      setShowConfirmDelete(true);
    }
  };
  const handleKeypress = useCallback((key: string) => {
    switch (key) {
      case 'ctrl+r':
        handleRefresh(true);
        break;
      case 'ctrl+s':
        setShowAdvSearchDialog(true);
        break;
      case 'ctrl+n':
        setShowNewDialog(true);
        break;
      default:
        break;
    }
  }, []);
  useKeyboardEventHandler({
    keys: ['ctrl+r', 'ctrl+s', 'ctrl+n'],
    handleFocusableElements: true,
    onKeyEvent: handleKeypress,
  });
  const handleRefresh = (_forceRefresh: boolean) => {
    if (paginationModel && filterModel && sortModel) {
      setLoading(true);
      onDataRefresh(
        configuration,
        disablePagination,
        _forceRefresh,
        filterModel,
        globalSearch,
        paginationModel,
        sortModel
      );
    }
  };
  const handleSchemaRefresh = () => {
    onSchemaRefresh(configuration);
  };
  const handleRowClick = (params: any) => {
    const getRowIdFn = configuration.getRowId || ((row: any) => row.id);
    const rowId = getRowIdFn(params.row);
    setRowSelectionModel(gridRowSelectionInclude([rowId]));
    setSelectedRows([params.row]);
    setRowsSelected(true);
    setShowUpdateDialog(true);
  };
  const handleButtonClick = (buttonName: string, row: any) => {
    if (buttonName === 'view') {
      // Create a DocumentStub from the row data
      const docStub: DocumentStub = {
        pageId: row.pageId || row.id,
        docName: row.docName || row.name || row.title || row.pageTitle,
        docType: 'Work Instruction', // Default type, could be made configurable
        pageNamespace: row.pageNamespace,
        fileDescription: row.fileDescription,
      };
      onDocView(docStub);
    }
  };

  // Hooks
  useEffect(() => {
    if (isOrgMismatch) {
      onDataClear(configuration);
      handleSchemaRefresh();
      handleRefresh(false);
      resolveMismatch();
    }
  }, [isOrgMismatch]);
  useEffect(() => {
    if (!schema) {
      handleSchemaRefresh();
    }
    handleRefresh(false);
  }, [configuration.itemType]);
  useEffect(() => {
    handleRefresh(forceRefresh);
  }, [filterModel]);
  useEffect(() => {
    handleRefresh(forceRefresh);
  }, [globalSearch]);
  useEffect(() => {
    handleRefresh(false);
  }, [paginationModel]);
  useEffect(() => {
    handleRefresh(false);
  }, [sortModel]);

  // Initialize filter/sort state from configuration when itemType changes (useEffect not useMemo
  // so we don't run setState during render; keeps default filter and chips stable on refresh).
  useEffect(() => {
    setGlobalSearch('');
    setDisablePagination(configuration.paginationDisabled || false);

    const newFilterModel: GridFilterModel = { items: [] };

    if (configuration.defaultFilterModel) {
      newFilterModel.items = configuration.defaultFilterModel.items;

      setAdvSearchFilterCount(configuration.defaultFilterModel.items.length);

      const newSearchFilters = configuration.defaultFilterModel.items.map(
        (filter) => ({
          fieldName: filter.field,
          operator: filter.operator as TOperators,
          value: Array.isArray(filter.value)
            ? filter.value.join(',')
            : (filter.value ?? ''),
          values:
            typeof filter.value === 'string'
              ? filter.value.split(',').map((v) => v.trim())
              : Array.isArray(filter.value)
                ? filter.value
                : [],
        })
      );
      setSearchFilters(newSearchFilters);
    } else {
      setAdvSearchFilterCount(0);
      setSearchFilters([
        {
          fieldName: '',
          operator: 'contains',
          value: '',
          values: [],
        },
      ]);
    }
    setFilterModel(newFilterModel);

    if (configuration.defaultSortModel) {
      setSortModel(configuration.defaultSortModel);
    } else {
      setSortModel([]);
    }
  }, [configuration.itemType]);
  useMemo(() => {
    if (!workingState?.loading) {
      setLoading(false);
    }

    // Only clear selection when THIS Base's data actually refreshes (not global workingState changes)
    // Note: We clear even when prev is null (after DATA_CLEAR) to handle refresh cycles properly
    if (
      data?.lastUpdated &&
      data.lastUpdated !== prevDataLastUpdatedRef.current
    ) {
      setRowSelectionModel(emptyGridRowSelectionModel());
      setSelectedRows([]);
      setRowsSelected(false);
    }
    prevDataLastUpdatedRef.current = data?.lastUpdated || null;

    if (
      !configuration.customDialogs?.new &&
      prevWorkingStateRef.current.adding &&
      !workingState?.adding &&
      !workingState?.error
    ) {
      setShowNewDialog(false);
    }

    if (
      !configuration.customDialogs?.update &&
      prevWorkingStateRef.current.updating &&
      !workingState.updating &&
      !workingState?.error
    ) {
      setShowUpdateDialog(false);
    }
    prevWorkingStateRef.current = workingState;
  }, [workingState, data?.lastUpdated]);
  useMemo(() => {
    // Schema stores fields as an object in the form { [fieldName]: { [columnName]: columnValue } }.
    // We need to convert this into an array of rows in the form [{ fieldName, ...columnValues }] and
    // store it in the fields state.
    if (schema?.fields) {
      let newFields = Object.keys(schema?.fields).map((fieldName) => ({
        fieldName,
        ...schema.fields[fieldName],
      }));

      // Omit any fields that are included in the inherentFilterModel of the configuration
      if (configuration.inherentFilterModel) {
        const inherentFields = configuration.inherentFilterModel.items.map(
          (item) => item.field
        );
        newFields = newFields.filter(
          (field) => !inherentFields.includes(field.fieldName)
        );
      }

      // For any fields with a type of 'enum', the data for the acceptable values is stored in a property
      // called 'values' and the cooresponding labels is stored in a property called 'valueLabels'. We need
      // to combine these two arrays into a single array of objects in the form { value, label } and store it
      // in a new array called valueMap.
      newFields.forEach((field) => {
        if (field.type === 'enum' && field.values && field.valueLabels) {
          field.valueMap = [];
          field.values.forEach((value, index) => {
            field.valueMap?.push({
              id: value,
              label: field.valueLabels?.[index] ?? value,
            });
          });
        }
      });

      setFields(newFields);
    }
  }, [schema]);

  // Custom Dialog Handling
  const ModImportDialog = configuration.customDialogs?.import
    ? configuration.customDialogs.import
    : ImportDialog;
  const ModNewDialog = configuration.customDialogs?.new
    ? configuration.customDialogs.new
    : NewDialog;
  const ModUpdateDialog = configuration.customDialogs?.update
    ? configuration.customDialogs.update
    : UpdateDialog;
  const ModPayrollExportDialog = configuration.customDialogs?.payrollExport;

  // Misc
  const mobileMenuId = 'base-menu-mobile';

  return (
    <Paper
      sx={{
        flexGrow: 1,
        borderRadius: 2,
      }}
    >
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar disableGutters sx={{ paddingLeft: 1 }}>
          <SearchWidget
            advSearchFilterCount={advSearchFilterCount}
            onClearClick={() => setGlobalSearch('')}
            onEnterKeyPress={(searchText) => setGlobalSearch(searchText)}
            setShowAdvSearchDialog={setShowAdvSearchDialog}
            value={globalSearch}
          />
          <Box sx={{ flexGrow: 1 }} />
          <StandardMenu
            configuration={configuration}
            handleExport={handleExport}
            handleRefresh={() => handleRefresh(true)}
            rowsSelected={rowsSelected}
            schema={schema}
            setShowConfirmDelete={setShowDeleteConfirmation}
            setShowImportDialog={setShowImportDialog}
            setShowPayrollExportDialog={setShowPayrollExportDialog}
            setShowNewDialog={setShowNewDialog}
            setShowUpdateDialog={setShowUpdateDialog}
          />
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={(e) => setMobileMoreAnchorEl(e.currentTarget)}
              color="inherit"
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <MobileMenu
        configuration={configuration}
        handleExport={handleExport}
        handleRefresh={() => handleRefresh(true)}
        mobileMenuId={mobileMenuId}
        mobileMoreAnchorEl={mobileMoreAnchorEl}
        onMobileMenuClose={() => setMobileMoreAnchorEl(null)}
        rowsSelected={rowsSelected}
        schema={schema}
        setShowConfirmDelete={setShowDeleteConfirmation}
        setShowImportDialog={setShowImportDialog}
        setShowPayrollExportDialog={setShowPayrollExportDialog}
        setShowNewDialog={setShowNewDialog}
        setShowUpdateDialog={setShowUpdateDialog}
      />
      <DataGrid
        configuration={configuration}
        data={data?.values || []}
        disablePagination={disablePagination}
        gridHeightCss={gridHeightCss}
        loading={loading}
        onButtonClick={handleButtonClick}
        onRowClick={handleRowClick}
        paginationModel={paginationModel}
        rowCount={data?.rowCount ?? -1}
        rowSelectionModel={rowSelectionModel}
        schema={schema}
        setPaginationModel={setPaginationModel}
        setRowsSelected={setRowsSelected}
        setRowSelectionModel={setRowSelectionModel}
        setSelectedRows={setSelectedRows}
        setSortModel={setSortModel}
        sortModel={sortModel}
      />

      <Suspense fallback={null}>
        {showImportDialog && (
          <ModImportDialog
            authtoken={authtoken}
            fields={fields}
            handleAdd={(r: any) => handleAdd(r)}
            handleCancel={() => setShowImportDialog(false)}
            itemLabel={configuration.itemLabel}
            open={showImportDialog}
            schema={schema}
            workingState={workingState}
          />
        )}

        {showPayrollExportDialog && ModPayrollExportDialog && (
          <ModPayrollExportDialog
            handleCancel={() => setShowPayrollExportDialog(false)}
            open={showPayrollExportDialog}
          />
        )}

        {showNewDialog && (
          <ModNewDialog
            authtoken={authtoken}
            configuration={configuration}
            disablePagination={disablePagination}
            fields={fields}
            filterModel={filterModel}
            globalSearch={globalSearch}
            handleAdd={(r: any) => handleAdd(r)}
            handleCancel={() => setShowNewDialog(false)}
            handleUpdate={(r: any) => handleUpdate(r)}
            itemLabel={configuration.itemLabel}
            open={showNewDialog}
            paginationModel={paginationModel}
            recordTemplate={configuration.recordTemplate}
            schema={schema}
            setPaginationModel={setPaginationModel}
            setShowNewDialog={setShowNewDialog}
            sortModel={sortModel}
            workingState={workingState}
          />
        )}

        {showUpdateDialog && (
          <ModUpdateDialog
            authtoken={authtoken}
            configuration={configuration}
            disablePagination={disablePagination}
            fields={fields}
            filterModel={filterModel}
            globalSearch={globalSearch}
            handleUpdate={(r: any) => handleUpdate(r)}
            handleCancel={handleCancelUpdate}
            itemLabel={configuration.itemLabel}
            open={showUpdateDialog}
            paginationModel={paginationModel}
            recordTemplate={configuration.recordTemplate}
            schema={schema}
            selectedRows={selectedRows}
            setShowUpdateDialog={setShowUpdateDialog}
            sortModel={sortModel}
            workingState={workingState}
          />
        )}

        {showDeleteDialog && configuration.customDialogs?.delete && (
          <configuration.customDialogs.delete
            authtoken={authtoken}
            configuration={configuration}
            fields={fields}
            handleCancel={() => setShowDeleteDialog(false)}
            handleDelete={(rows: any[]) => {
              setShowDeleteDialog(false);
              handleDeleteRows(rows);
            }}
            handleUpdate={(rows: any[]) => handleUpdate(rows)}
            itemLabel={configuration.itemLabel}
            open={showDeleteDialog}
            schema={schema}
            selectedRows={selectedRows}
            workingState={workingState}
          />
        )}
      </Suspense>

      <ConfirmationDialog
        description={`Are you sure you wish to delete the selected ${getPluralLabel(
          configuration.itemLabel
        )}`}
        handleNo={() => setShowConfirmDelete(false)}
        handleYes={handleConfirmDelete}
        open={showConfirmDelete}
        title="Delete Confirmation"
      />

      {showAdvSearchDialog && (
        <AdvSearchDialog
          disablePagination={disablePagination}
          fields={fields}
          forceRefresh={forceRefresh}
          handleCancel={() => setShowAdvSearchDialog(false)}
          handleSave={handleAdvSearchSave}
          open={showAdvSearchDialog}
          schema={schema}
          searchFilters={searchFilters}
          setDisablePagination={setDisablePagination}
          setForceRefresh={setForceRefresh}
          setSearchFilters={setSearchFilters}
        />
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={workingState.deleting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
}
